import React, { useRef, useEffect, useState } from "react";
import { getComparisonChartFields } from "requests/comparisonChart";
import { ComparisonChartField } from "interfaces/Mueller.Business.Models";
import { MTable, MSpinner, MCard } from "components/Shared";
import { createRequestCoalescer } from "tools/requestCoalescer";

const comparisonChartCoalescer = createRequestCoalescer<ComparisonChartField[], number>();
const activeRequest = { current: true };

interface ComparisonChartProps {
  controlNumber: number
}

const ComparisonChart = ({ controlNumber }: ComparisonChartProps) => {
  const mounted = useRef(true);
  const [fetchingComparisonFields, setFetchingComparisonFields] = useState(false);
  const [comparisonFields, setComparisonFields] = useState<ComparisonChartField[]>(null);

  const updateComparisonFields = () => comparisonChartCoalescer.run(
    controlNumber,
    (resolve, reject) => getComparisonChartFields(controlNumber, resolve, reject, activeRequest),
    successCallback,
    errorCallback,
  );

  const successCallback = (response: ComparisonChartField[]) => {
    if (mounted.current) {
      setComparisonFields(response);
      setFetchingComparisonFields(false);
    }
  };

  const errorCallback = () => {
    if (mounted.current) {
      setComparisonFields([]);
      setFetchingComparisonFields(false);
    }
  };

  useEffect(() => {
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setFetchingComparisonFields(true);
    updateComparisonFields();
  }, [controlNumber]);

  const mapData = () => comparisonFields?.map(x => ({
    name: x.name?.length ? x.name : "-",
    expectedValue: x.expectedValue?.length ? x.expectedValue : "-",
    actualValue: x.actualValues?.length ? x.actualValues.join(", ") : "-",
  }));
  
  return (<MCard title="Comparison Chart" className="comparison-chart">
    {fetchingComparisonFields || !comparisonFields ? <MSpinner /> : (
      <MTable
        key="comparison-table"
        columnDefinitions={[
          { name: "Name", dataSource: "name" },
          { name: "Expected", dataSource: "expectedValue" },
          { name: "Actual", dataSource: "actualValue" },
        ]}
        data={mapData()}
      />
    )}
  </MCard>);
};

export default ComparisonChart;